'use client';
import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    OneSignalDeferred?: {
      push: (callback: (OneSignal: any) => void) => void;
    };
  }
}

interface UseOneSignalOptions {
  userId?: string | number;
  email?: string;
}

export function useOneSignalPush({ userId, email }: UseOneSignalOptions): void {
  const hasInitialized = useRef(false);

  const logoutUser = async () => {
    if (hasInitialized.current && window.OneSignalDeferred) {
      window.OneSignalDeferred.push(async (OneSignal) => {
        try {
          await OneSignal.logout();
          console.log('OneSignal user logged out');
        } catch (error) {
          console.error('OneSignal logout error:', error);
        }
      });
      hasInitialized.current = false;
    }
  };

  // Handle user login and setup
  useEffect(() => {
    // Only proceed if we have both userId and email, and haven't initialized yet
    if (
      !(userId && email) ||
      hasInitialized.current ||
      typeof window === 'undefined'
    ) {
      return;
    }

    // Check if OneSignal is available
    if (!window.OneSignalDeferred) {
      console.warn('OneSignal not available');
      return;
    }

    hasInitialized.current = true;

    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        // Login user and add email
        await OneSignal.login(`${userId}`);
        await OneSignal.User.addEmail(email);

        // Subscribe to push notifications

        // Check permission status first
        const permStatus = await OneSignal.Notifications.permission;

        // Opt-in to push notifications
        const pushOptIn = await OneSignal.User.PushSubscription.optedIn;

        console.log(
          'OneSignal user setup completed:',
          userId,
          permStatus,
          pushOptIn
        );
      } catch (error) {
        console.error('OneSignal setup error:', error);
        hasInitialized.current = false; // Allow retry on error
      }
    });

    return () => {
      // Cleanup on component unmount
      logoutUser();
    };
  }, [userId, email]);
}
